import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

/*
 * Every note attached to a staff member, from both places they live:
 *
 *   HR     — the file kept ABOUT a worker: warnings, conversations, reviews.
 *   Shift  — what the worker WROTE on their shifts.
 *
 * This screen used to read the HR table alone, so it said "No Data Available" for a worker with
 * hundreds of shift notes and no HR file — which is most workers. Anyone opening "Employee Notes"
 * is after the notes attached to that employee, so both are here and either can be filtered out.
 */

const fmtDate = (v) => {
  if (!v) return '–';
  try { return format(parseISO(String(v).slice(0, 10)), 'd MMM yyyy'); } catch { return String(v); }
};

const fmtTime = (v) => (v ? String(v).slice(0, 5) : '');

const sourceBadge = (v) => (
  <span className={clsx('px-2 py-0.5 rounded-full text-xs font-semibold',
    v === 'Shift' ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700')}>
    {v || '–'}
  </span>
);

const COLS = [
  { key: 'note_date', label: 'Date', render: (v) => <span className="whitespace-nowrap">{fmtDate(v)}</span> },
  { key: 'source', label: 'Source', render: sourceBadge },
  { key: 'staff_name', label: 'Staff Name' },
  { key: 'role', label: 'Role', render: (v) => (v ? v.replace(/_/g, ' ') : '–') },
  { key: 'client_name', label: 'Participant', render: (v) => v || <span className="text-slate-400">–</span> },
  {
    key: 'shift_date',
    label: 'Shift',
    render: (v, row) => (v
      ? <span className="whitespace-nowrap">{fmtDate(v)}<span className="block text-[11px] text-slate-400">{fmtTime(row.start_time)}–{fmtTime(row.end_time)}</span></span>
      : <span className="text-slate-400">–</span>),
  },
  { key: 'note_type', label: 'Type', render: (v) => v || '–' },
  {
    key: 'note',
    label: 'Note',
    render: (v) => (v
      ? <span title={v} className="whitespace-pre-wrap max-w-md block text-xs">{v.slice(0, 300)}{v.length > 300 ? '…' : ''}</span>
      : <span className="text-slate-400">–</span>),
  },
  { key: 'created_by_name', label: 'Written By' },
];

export default function EmployeeNotesReport() {
  const [data, setData] = useState([]);
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart] = useState(format(subDays(new Date(), 90), 'yyyy-MM-dd'));
  const [endDate, setEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [staffId, setStaffId] = useState('');
  const [clientId, setClientId] = useState('');
  const [source, setSource] = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (staffId) p.set('staff_id', staffId);
    if (clientId) p.set('client_id', clientId);
    if (source) p.set('source', source);
    api.get(`/reports/employee-notes?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, staffId, clientId, source]);

  useEffect(() => { load(); }, [load]);

  const selStaff = staff.find(s => String(s.id) === String(staffId));
  const selClient = clients.find(c => String(c.id) === String(clientId));

  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selStaff ? [{ label: 'Staff', value: `${selStaff.first_name} ${selStaff.last_name}`, onRemove: () => setStaffId('') }] : []),
    ...(selClient ? [{ label: 'Participant', value: `${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
    ...(source ? [{ label: 'Source', value: source === 'hr' ? 'HR notes' : 'Shift notes', onRemove: () => setSource('') }] : []),
  ];

  const shiftCount = data.filter(r => r.source === 'Shift').length;
  const hrCount = data.filter(r => r.source === 'HR').length;

  return (
    <ReportLayout
      title={selStaff ? `Employee Notes — ${selStaff.first_name} ${selStaff.last_name}` : 'Employee Notes — All Employees'}
      data={data} filename="employee_notes_report" columns={COLS}
      activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Employee (optional)</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Employees</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Participant (optional)</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Participants</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Note Source</label>
            <select value={source} onChange={e => setSource(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Notes</option>
              <option value="shift">Shift notes only</option>
              <option value="hr">HR notes only</option>
            </select></div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }>
      <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600 font-medium">
        <span>Total: <strong className="text-slate-900">{data.length}</strong></span>
        <span>Shift notes: <strong className="text-slate-900">{shiftCount}</strong></span>
        <span>HR notes: <strong className="text-slate-900">{hrCount}</strong></span>
        {selClient && (
          <span className="text-slate-400 text-xs">
            Filtering by participant shows shift notes only — an HR note is not about a participant.
          </span>
        )}
      </div>
      <ReportTable
        columns={COLS} data={data} loading={loading} pagination={pagination}
        emptyMsg="No notes for this employee in this period."
      />
    </ReportLayout>
  );
}
